import {computed, inject, Injectable, signal} from '@angular/core';
import {CpruTab} from '../types/CpruTab';
import {RoutesAddonsEnums, RoutesEnums} from '../enums/RoutesEnums';
import {ToolsService} from './tools.service';
import {WindowSizeService} from './window-size.service';
import {Router} from '@angular/router';
import {SearchService} from './search/search.service';
import {SearchState} from '../types/SearchState';
import {UrlBuilderService} from './url-builder.service';
import {QueryParams} from '../types/QueryParams';
import {SearchReloadService} from './search/search-reload.service';
import {SearchWidgetFiltersPageEnum} from '../components/search-widget-v3/enums/search-widget-filters-page.enum';

@Injectable()
export class CpruTabsService {
  public highlightedSection = signal<RoutesAddonsEnums | string>(null);
  private readonly tabsKeysSectionsResolversMap: Array<Function> = [];
  private allTabs = signal<CpruTab[]>(null);

  private windowSize = inject(WindowSizeService);
  private size = this.windowSize.windowSize;
  private readonly mobileTabsThreshold = 992;
  private isMobile = computed<boolean>(() => this.size()[0] < this.mobileTabsThreshold);

  public tabs = computed<CpruTab[]>(() => {
    return this.allTabs()
      ? this.allTabs().filter((tab) => {
          return this.isMobile() ? tab.mobileOnly || !tab.desktopOnly : tab.desktopOnly || !tab.mobileOnly;
        })
      : [];
  });

  constructor(
    private tools: ToolsService,
    private router: Router,
    private searchService: SearchService,
    private urlBuilder: UrlBuilderService,
    private reloadService: SearchReloadService,
  ) {
    this.generateTabsKeysSectionsResolversMap();
  }

  public changeTabAndScrollToSection(tabName: RoutesAddonsEnums | string): void {
    this.scrollToSection(tabName);
    this.highlightedSection.set(tabName);
  }

  public handleTabsIndexChange(tabName: RoutesAddonsEnums | string): void {
    if (this.tabsKeysSectionsResolversMap[tabName]) {
      this.tabsKeysSectionsResolversMap[tabName]();
    } else {
      this.changeTabAndScrollToSection(tabName);
    }
  }

  public updateTabs(tabs: CpruTab[]): void {
    this.allTabs.set(tabs);
  }

  public setHighlightedSection(tabName: RoutesAddonsEnums | string): void {
    this.highlightedSection.set(tabName);
  }

  private generateTabsKeysSectionsResolversMap(): void {
    this.tabsKeysSectionsResolversMap[RoutesAddonsEnums.SERP_SERVICES] = () =>
      this.updateSearchStateAndRedirect(RoutesAddonsEnums.SERP_SERVICES);
    this.tabsKeysSectionsResolversMap[RoutesAddonsEnums.SERP_CAREPROVIDERS] = () =>
      this.updateSearchStateAndRedirect(RoutesAddonsEnums.SERP_CAREPROVIDERS);

    this.tabsKeysSectionsResolversMap[SearchWidgetFiltersPageEnum.CARE_SERVICE_UNIT_GROUPS] = () =>
      this.highlightedSection.set(SearchWidgetFiltersPageEnum.CARE_SERVICE_UNIT_GROUPS);
    this.tabsKeysSectionsResolversMap[SearchWidgetFiltersPageEnum.CARE_SERVICE_TYPE_GROUPS] = () =>
      this.highlightedSection.set(SearchWidgetFiltersPageEnum.CARE_SERVICE_TYPE_GROUPS);
    this.tabsKeysSectionsResolversMap[SearchWidgetFiltersPageEnum.SYMPTOMS_GROUPS] = () =>
      this.highlightedSection.set(SearchWidgetFiltersPageEnum.SYMPTOMS_GROUPS);
  }

  private scrollToSection(tabName: RoutesAddonsEnums | string): void {
    const section = `section-${tabName}`;
    let correction = -150;
    this.tools.scrollTo(section, 400, correction);
  }

  private updateSearchStateAndRedirect(tabName: RoutesAddonsEnums): void {
    const state: SearchState = this.searchService.searchState();
    if (tabName.toString() !== state.tab) {
      const baseUrl: string[] = ['/', RoutesEnums.SEARCH];
      state.tab = tabName.toString();
      const routerLink: string[] = this.urlBuilder.CreateSearchResultsRouterLink(state, baseUrl);
      const queryParams: QueryParams = this.urlBuilder.CreateSearchResultsQueryParams(state);
      this.reloadService.reload(routerLink, queryParams);
      this.router.navigate(routerLink, {queryParams});
    }
    this.highlightedSection.set(tabName);
  }
}
